/**
 * src/api/sessions.ts
 * --------------------
 * API functions for managing Helix chat sessions.
 *
 * Fully implemented this will:
 *   - createSession(orgId) — POST /sessions — creates a new chat session
 *     and returns a Session object with id, title, createdAt, isAnon flag.
 *   - listSessions() — GET /sessions — returns the authenticated user's
 *     session history, sorted by updatedAt descending.
 *   - getSession(id) — GET /sessions/{id} — returns a single session with
 *     metadata (title, summary, messageCount, lastMessage).
 *   - renameSession(id, title) — PATCH /sessions/{id} — updates session title.
 *   - deleteSession(id) — DELETE /sessions/{id} — removes session and its messages.
 *   - mergeAnonSession(anonId, userId) — POST /sessions/merge — transfers an
 *     anonymous session to an authenticated user after sign-in.
 *
 * Key dependencies:
 *   src/api/client.ts, src/types/session.ts
 */
import apiClient from './client'
import type { Session } from '@/types/session'

type SessionResponse = Session & {
  org_id?: string
  user_id?: string
  is_anonymous?: boolean
  created_at?: string
  updated_at?: string
  last_message?: string
  message_count?: number
}

function normalizeSession(session: SessionResponse): Session {
  return {
    ...session,
    orgId: session.orgId ?? session.org_id ?? '',
    userId: session.userId ?? session.user_id,
    isAnonymous: session.isAnonymous ?? session.is_anonymous ?? true,
    createdAt: session.createdAt ?? session.created_at ?? new Date().toISOString(),
    updatedAt: session.updatedAt ?? session.updated_at ?? new Date().toISOString(),
    lastMessage: session.lastMessage ?? session.last_message,
    messageCount: session.messageCount ?? session.message_count,
  }
}

/** Create a new chat session (anonymous or authenticated). */
export async function createSession(orgId: string): Promise<Session> {
  const { data } = await apiClient.post<SessionResponse>('/sessions', { org_id: orgId })
  return normalizeSession(data)
}

/** List all sessions for the current user. */
export async function listSessions(): Promise<Session[]> {
  const { data } = await apiClient.get<SessionResponse[]>('/sessions')
  return data.map(normalizeSession)
}

/** Fetch a single session by ID. */
export async function getSession(sessionId: string): Promise<Session> {
  const { data } = await apiClient.get<SessionResponse>(`/sessions/${sessionId}`)
  return normalizeSession(data)
}

/** Rename a session. */
export async function renameSession(
  sessionId: string,
  title: string,
): Promise<Session> {
  const { data } = await apiClient.patch<SessionResponse>(`/sessions/${sessionId}`, {
    title,
  })
  return normalizeSession(data)
}

/** Delete a session and all of its messages. */
export async function deleteSession(sessionId: string): Promise<void> {
  await apiClient.delete(`/sessions/${sessionId}`)
}

/**
 * Merge an anonymous session into an authenticated user account.
 * Called immediately after sign-in when an anonSessionId exists in localStorage.
 */
export async function mergeAnonSession(
  anonSessionId: string,
): Promise<Session> {
  const { data } = await apiClient.post<SessionResponse>('/sessions/merge', {
    anon_session_id: anonSessionId,
  })
  return normalizeSession(data)
}
